import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import type { Database } from '@/types/supabase'
import { supabase } from '@/lib/supabase/client'
import { fetchAllRows } from '@/lib/supabase/fetch-all-rows'
import { forEachIdChunk } from '@/lib/supabase/id-chunks'
import { ilikeAnyColumn, searchWords } from '@/lib/supabase/search-filter'

// =============================================================================
// Types
// =============================================================================

type OrgProductRow = Omit<
  Database['public']['Tables']['org_products']['Row'],
  'attributes'
> & {
  /** Imported columns without a fixed field, keyed by spreadsheet header */
  attributes: Record<string, string>
}
export type OrgProductImage =
  Database['public']['Tables']['org_product_images']['Row']

export type OrgProductMediatype = 'product' | 'sketch'

export interface OrgProductWithImages extends OrgProductRow {
  org_product_images: OrgProductImage[]
}

export interface OrgProductFilterOptions {
  colorways: string[]
  collections: string[]
  genders: string[]
  main_groups: string[]
  sub_groups: string[]
  categories: string[]
  /** Catalogue attribute name → its values in the organisation */
  attributes: Record<string, string[]>
}

export type OrgProduct = OrgProductRow

export interface OrgProductFilters {
  searchQuery?: string | null
  colorway?: string | null
  collection?: string | null
  gender?: string | null
  mainGroup?: string | null
  subGroup?: string | null
  category?: string | null
  /** Catalogue attributes that must all match */
  attributes?: Record<string, string> | null
  mediatype?: OrgProductMediatype | null
}

export interface OrgProductPage {
  products: OrgProduct[]
  totalCount: number
}

export interface OrgProductWithImagesPage {
  products: OrgProductWithImages[]
  totalCount: number
}

const PAGE_SIZE = 16
const MIN_SEARCH_WORD_LENGTH = 2

// =============================================================================
// Query helpers
// =============================================================================

function buildOrgProductSelectQuery(
  orgId: string,
  options?: { count?: 'exact' }
) {
  return supabase
    .from('org_products')
    .select('*', options)
    .eq('org_id', orgId)
    .is('deleted_at', null)
    .order('updated_at', { ascending: false })
}

function applyOrgProductFilters<
  T extends {
    eq: (col: string, val: string) => T
    or: (filter: string) => T
    contains: (col: string, val: Record<string, string>) => T
  },
>(query: T, filters?: OrgProductFilters): T {
  let q = query

  if (filters?.searchQuery) {
    for (const word of searchWords(
      filters.searchQuery,
      MIN_SEARCH_WORD_LENGTH
    )) {
      q = q.or(ilikeAnyColumn(['name', 'article_number', 'colorway'], word))
    }
  }

  if (filters?.colorway) {
    q = q.eq('colorway', filters.colorway)
  }

  if (filters?.collection) {
    q = q.eq('collection', filters.collection)
  }

  if (filters?.gender) {
    q = q.eq('gender', filters.gender)
  }

  if (filters?.mainGroup) {
    q = q.eq('main_group', filters.mainGroup)
  }

  if (filters?.subGroup) {
    q = q.eq('sub_group', filters.subGroup)
  }

  if (filters?.category) {
    q = q.eq('category', filters.category)
  }

  if (filters?.attributes && Object.keys(filters.attributes).length > 0) {
    q = q.contains('attributes', filters.attributes)
  }

  return q
}

// =============================================================================
// Queries & Mutations
// =============================================================================

/** Undefined optional fields are not written, so an upsert keeps their stored value. */
export interface CreateOrgProductInput {
  name: string
  article_number: string
  colorway?: string
  collection?: string | null
  gender?: string | null
  main_group?: string | null
  sub_group?: string | null
  category?: string | null
  /** Merged into the stored attributes; a null value removes its key */
  attributes?: Record<string, string | null>
}

export const orgProduct = {
  findOne: (productId: string) =>
    queryOptions({
      queryKey: ['org-product', productId],
      queryFn: async (): Promise<OrgProductWithImages> => {
        if (!productId || productId.trim() === '') {
          throw new Error(
            'Invalid productId: cannot fetch org product without valid ID'
          )
        }

        const { data, error } = await supabase
          .from('org_products')
          .select('*, org_product_images(*)')
          .eq('id', productId)
          .is('deleted_at', null)
          .single()

        if (error) {
          throw new Error(`Failed to fetch org product: ${error.message}`, {
            cause: error,
          })
        }

        const product = data as unknown as OrgProductWithImages
        product.org_product_images.sort(
          (a, b) =>
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        )
        return product
      },
      staleTime: 5 * 60 * 1000,
    }),

  findManyWithImagesByOrg: (orgId: string, filters?: OrgProductFilters) =>
    infiniteQueryOptions({
      queryKey: ['org-products', orgId, 'with-images', filters ?? {}],
      queryFn: async ({ pageParam = 0 }): Promise<OrgProductWithImagesPage> => {
        if (!orgId || orgId.trim() === '') {
          throw new Error(
            'Invalid orgId: cannot fetch org products without valid organization ID'
          )
        }

        const from = pageParam * PAGE_SIZE
        const to = from + PAGE_SIZE - 1

        let baseQuery = supabase
          .from('org_products')
          .select('*, org_product_images!inner(*)', { count: 'exact' })
          .eq('org_id', orgId)
          .is('deleted_at', null)
          .order('updated_at', { ascending: false })

        if (filters?.mediatype) {
          baseQuery = baseQuery.eq(
            'org_product_images.category',
            filters.mediatype
          )
        }

        const query = applyOrgProductFilters(baseQuery, filters)

        const { data, count, error } = await query.range(from, to)

        if (error) {
          throw new Error(`Failed to fetch org products: ${error.message}`, {
            cause: error,
          })
        }

        return {
          products: (data || []) as unknown as OrgProductWithImages[],
          totalCount: count ?? 0,
        }
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.products.length === PAGE_SIZE ? allPages.length : undefined,
    }),

  findAllWithImagesByOrg: (orgId: string, filters?: OrgProductFilters) =>
    queryOptions({
      queryKey: ['org-products', orgId, 'with-images', 'all', filters ?? {}],
      queryFn: async (): Promise<OrgProductWithImages[]> => {
        if (!orgId || orgId.trim() === '') {
          throw new Error(
            'Invalid orgId: cannot fetch org products without valid organization ID'
          )
        }

        let baseQuery = supabase
          .from('org_products')
          .select('*, org_product_images!inner(*)')
          .eq('org_id', orgId)
          .is('deleted_at', null)
          .order('updated_at', { ascending: false })

        if (filters?.mediatype) {
          baseQuery = baseQuery.eq(
            'org_product_images.category',
            filters.mediatype
          )
        }

        const query = applyOrgProductFilters(baseQuery, filters)

        const { data, error } = await query

        if (error) {
          throw new Error(`Failed to fetch org products: ${error.message}`, {
            cause: error,
          })
        }

        return (data || []) as unknown as OrgProductWithImages[]
      },
    }),

  findManyByOrg: (orgId: string, filters?: OrgProductFilters) =>
    infiniteQueryOptions({
      queryKey: ['org-products', orgId, filters ?? {}],
      queryFn: async ({ pageParam = 0 }): Promise<OrgProductPage> => {
        if (!orgId || orgId.trim() === '') {
          throw new Error(
            'Invalid orgId: cannot fetch org products without valid organization ID'
          )
        }

        const from = pageParam * PAGE_SIZE
        const to = from + PAGE_SIZE - 1

        const query = applyOrgProductFilters(
          buildOrgProductSelectQuery(orgId, { count: 'exact' }),
          filters
        )

        const { data, count, error } = await query.range(from, to)

        if (error) {
          throw new Error(`Failed to fetch org products: ${error.message}`, {
            cause: error,
          })
        }

        return {
          products: (data || []) as OrgProduct[],
          totalCount: count ?? 0,
        }
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.products.length === PAGE_SIZE ? allPages.length : undefined,
    }),

  findFilterOptions: (orgId: string) =>
    queryOptions({
      queryKey: ['org-products', orgId, 'filters'],
      queryFn: async (): Promise<OrgProductFilterOptions> => {
        if (!orgId || orgId.trim() === '') {
          throw new Error(
            'Invalid orgId: cannot fetch org product filters without valid organization ID'
          )
        }

        const { data, error } = await supabase.rpc(
          'get_org_product_filter_options',
          { p_org_id: orgId }
        )

        if (error) {
          throw new Error(
            `Failed to fetch org product filters: ${error.message}`,
            { cause: error }
          )
        }

        const defaults: OrgProductFilterOptions = {
          colorways: [],
          collections: [],
          genders: [],
          main_groups: [],
          sub_groups: [],
          categories: [],
          attributes: {},
        }

        return data ? (data as unknown as OrgProductFilterOptions) : defaults
      },
      staleTime: 5 * 60 * 1000,
    }),

  createMany: async (
    orgId: string,
    items: CreateOrgProductInput[]
  ): Promise<OrgProduct[]> => {
    if (!orgId || orgId.trim() === '') {
      throw new Error(
        'Invalid orgId: cannot create org products without valid organization ID'
      )
    }

    if (items.length === 0) {
      return []
    }

    const allUpserted: OrgProduct[] = []
    const BATCH_SIZE = 500
    for (let i = 0; i < items.length; i += BATCH_SIZE) {
      const batch = items.slice(i, i + BATCH_SIZE)
      // Keys must be absent, not undefined: the upsert's column list is built
      // from the keys, and a listed column missing from a row is written as null.
      const { data, error } = await supabase
        .from('org_products')
        .upsert(
          batch.map((item) => ({
            org_id: orgId,
            name: item.name || item.article_number || 'Untitled',
            article_number: item.article_number,
            colorway: item.colorway ?? '',
            ...(item.collection !== undefined && {
              collection: item.collection,
            }),
            ...(item.gender !== undefined && { gender: item.gender }),
            ...(item.main_group !== undefined && {
              main_group: item.main_group,
            }),
            ...(item.sub_group !== undefined && { sub_group: item.sub_group }),
            ...(item.category !== undefined && { category: item.category }),
            ...(item.attributes !== undefined && {
              attributes: item.attributes,
            }),
            deleted_at: null,
          })),
          {
            onConflict: 'org_id, article_number, colorway',
            ignoreDuplicates: false,
          }
        )
        .select()

      if (error) {
        throw new Error(`Failed to upsert org products: ${error.message}`, {
          cause: error,
        })
      }

      allUpserted.push(...((data || []) as OrgProduct[]))
    }

    return allUpserted
  },

  delete: async (productId: string): Promise<void> => {
    if (!productId || productId.trim() === '') {
      throw new Error(
        'Invalid productId: cannot delete org product without valid ID'
      )
    }

    const { error } = await supabase
      .from('org_products')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', productId)

    if (error) {
      throw new Error(`Failed to delete org product: ${error.message}`, {
        cause: error,
      })
    }
  },

  deleteMany: async (productIds: string[]): Promise<void> => {
    if (!productIds.length) {
      return
    }

    const { error } = await supabase
      .from('org_products')
      .update({ deleted_at: new Date().toISOString() })
      .in('id', productIds)

    if (error) {
      throw new Error(`Failed to delete org products: ${error.message}`, {
        cause: error,
      })
    }
  },

  /** Imported images of the given products, with the spreadsheet value each came from. */
  findImageSourcesByProducts: async (
    productIds: string[]
  ): Promise<Array<{ org_product_id: string; source: string | null }>> => {
    const rows: Array<{ org_product_id: string; source: string | null }> = []
    await forEachIdChunk(productIds, async (chunk) => {
      rows.push(
        ...(await fetchAllRows(
          (from, to) =>
            supabase
              .from('org_product_images')
              .select('org_product_id, source')
              .in('org_product_id', chunk)
              .not('source', 'is', null)
              .order('id')
              .range(from, to),
          'Failed to fetch imported images'
        ))
      )
    })
    return rows
  },

  addImage: async (
    productId: string,
    storagePath: string,
    category: OrgProductMediatype = 'product',
    source: string | null = null
  ): Promise<void> => {
    if (!productId || productId.trim() === '') {
      throw new Error(
        'Invalid productId: cannot add image without valid product ID'
      )
    }

    // The same source on one product is a no-op; a null source never conflicts.
    const { error } = await supabase.from('org_product_images').upsert(
      {
        org_product_id: productId,
        storage_path: storagePath,
        category,
        source,
      },
      { onConflict: 'org_product_id,source', ignoreDuplicates: true }
    )

    if (error) {
      throw new Error(`Failed to add org product image: ${error.message}`, {
        cause: error,
      })
    }
  },

  deleteImage: async (imageId: string): Promise<void> => {
    const { error } = await supabase
      .from('org_product_images')
      .delete()
      .eq('id', imageId)
    if (error) {
      throw new Error(`Failed to delete image: ${error.message}`, {
        cause: error,
      })
    }
  },

  deleteAllByOrg: async (
    orgId: string,
    filters?: OrgProductFilters
  ): Promise<void> => {
    if (!orgId || orgId.trim() === '') {
      throw new Error(
        'Invalid orgId: cannot delete org products without valid organization ID'
      )
    }

    const baseQuery = supabase
      .from('org_products')
      .update({ deleted_at: new Date().toISOString() })
      .eq('org_id', orgId)
      .is('deleted_at', null)

    const { error } = await applyOrgProductFilters(baseQuery, filters)

    if (error) {
      throw new Error(`Failed to delete org products: ${error.message}`, {
        cause: error,
      })
    }
  },
}
